/**
 * index.tsx - Main Application Entry Point
 * 
 * INITIALIZATION ORDER:
 * 1. AppInitializer - Loads public data (shares/symbols) - NO AUTH REQUIRED
 * 2. AuthProvider - Handles authentication state
 * 3. Router - Handles routing
 * 4. Skeleton - Main app layout and components
 * 
 * This ensures that:
 * - Shares data is loaded BEFORE anything else (public, no auth needed)
 * - Auth state is available to all components
 * - Routing works correctly
 * - User can see public market data even without logging in
 */



import React from "react";
import * as ReactDOM from 'react-dom';
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from 'react-router-dom';

// Import CSS
import 'bootstrap/dist/css/bootstrap.min.css';
import "../assets/style/skeleton.css";
import "../assets/style/website.css";

// Core Providers
import { AppInitializer } from '../sigma/data/AppInitializer';
import { AuthProvider } from '../sigma/auth/Authcontext';

// Root path
import { ROOT_PATH } from "../sigma/Constants";


// Main App Component
const App = React.lazy(() => import('../layouts/Skeleton_New'));

// ============================================================================
// ROOT COMPONENT WITH PROPER PROVIDER ORDER
// ============================================================================

const Root: React.FC = () => {
    return (
        <React.StrictMode>
            {/* 
        STEP 1: AppInitializer
        - Loads shares/symbols from SHARES_XML
        - This is PUBLIC data, no authentication required
        - Shows loading spinner while fetching
        - Must complete before app renders
      */}
            <AppInitializer>
                {/* 
          STEP 2: AuthProvider
          - Manages authentication state
          - Provides user data to all components
          - Handles login/logout
          - Checks existing session
        */}
                <AuthProvider>
                    {/* 
            STEP 3: Router
            - Handles all routing
            - Enables navigation between pages
          */}
                    <Router basename={ROOT_PATH}>
                        {/* 
              STEP 4: Main App (Skeleton)
              - Main application layout
              - Uses data from AppInitializer (symbols)
              - Uses data from AuthProvider (user)
              - Renders all pages and components
            */}
                        <React.Suspense fallback={
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh'
                            }}>
                                <div className="spinner-border text-primary" role="status">
                                </div>
                                <span className='mx-2'>Loading Market Symbols...</span>
                            </div>
                        }>
                            <App />
                        </React.Suspense>
                    </Router>
                </AuthProvider>
            </AppInitializer>
        </React.StrictMode>
    );
};

// ============================================================================
// MOUNT THE APP
// ============================================================================

const rootElement = document.getElementById("root");
if (!rootElement) {
    throw new Error('Failed to find the root element');
}

const root = createRoot(rootElement);
root.render(<Root />);

if (module.hot) {
    const render = (react, elemId) => {
        ReactDOM.unmountComponentAtNode(document.getElementById(elemId) as HTMLInputElement);
        ReactDOM.render(React.createElement(react), document.getElementById(elemId));
    }
    module.hot.accept('./Index_new.tsx',
        () => render(require('./Index_new').default, 'root'));
    module.hot.accept();
}

// ============================================================================
// HOW IT WORKS
// ============================================================================

/*

INITIALIZATION FLOW:
====================

1. AppInitializer mounts
   ?? Calls loadAllShares()
   ?? Fetches SHARES_XML (public, no auth needed)
   ?? Parses XML into Quote objects
   ?? Stores in SharesContext
   ?? Shows loading spinner until complete

2. Once shares loaded, AppInitializer renders children
   ?? AuthProvider mounts
      ?? Checks localStorage for existing sessionId
      ?? If found, validates session with API
      ?? If valid, restores user state
      ?? If not, shows login page

3. Once auth ready, AuthProvider renders children
   ?? Router mounts
      ?? Skeleton mounts
         ?? Has access to symbols via useShares()
         ?? Has access to user via useAuth()
         ?? Fetches custody list (if authenticated)
         ?? Renders main app layout
         ?? Renders all routes and components

DATA AVAILABILITY:
==================

At any point in the app:

// Get shares data (loaded by AppInitializer)
const { symbols, isLoading } = useShares();

// Get auth data (managed by AuthProvider)
const { user, isAuthenticated, hasPermission } = useAuth();

// Use in components
<MarketPlatform
  symbols={symbols}                    // from useShares()
  custodyList={custodyList}            // from Skeleton state
  userSession={user?.sessionId}        // from useAuth()
  marginUser={user?.marginUser}        // from useAuth()
  isDemoAccount={user?.isDemoAccount}  // from useAuth()
/>

PUBLIC VS PROTECTED DATA:
=========================

PUBLIC (Level 0 - No Auth):
- symbols (shares data)          ? Loaded by AppInitializer
- Market summary, news, research ? Accessible without login
- Sector data, events            ? Accessible without login

PROTECTED (Level 1 - Client):
- custodyList                    ??  Loaded by Skeleton after auth
- watchlist                      ??  Requires authentication
- user.mainAccount               ??  Requires authentication
- user.otherAccounts             ??  Requires authentication
- user.marginUser                ??  Requires authentication
- user.isDemoAccount             ??  Requires authentication

BENEFITS OF THIS ARCHITECTURE:
==============================

? Clean separation of concerns
? Public data loads first (faster perceived load time)
? Auth state managed centrally
? Easy to access data anywhere
? Type-safe with TypeScript
? No prop drilling needed
? Easy to test
? Follows React best practices

*/

export default Root;